<template>
    <div>
        <limitless-page-header :loading="busy_loading" :title="'Dokument'" :sub-title="document_name" :title-id="document_id">
            <template #header-elements>
                <limitless-page-header-button url="/documents" icon="icon-circle-left2">Tillbaka</limitless-page-header-button>
            </template>
        </limitless-page-header>

        <div class="content">
            <tb-skeleton v-if="busy_loading" :height="100" :theme="'opacity'" :shape="'radius'" bg-color="#dcdbdc"></tb-skeleton>

            <div v-else-if="!busy_loading && document">
                <div class="row">
                    <div class="col-lg-6">
                        <div class="card">
                            <div class="card-body">
                                <ValidationObserver ref="documentform">
                                    <form @submit.prevent="saveDocument">
                                        <div class="row">
                                            <label class="col-form-label col-lg-2">Namn: <span class="text-danger">*</span></label>
                                            <div class="col-lg-10">
                                                <input-with-validation
                                                    vid="name"
                                                    label="Namn"
                                                    rules="required"
                                                    v-model="document.name"
                                                    hide-label
                                                />
                                            </div>
                                        </div>
                                        <div v-if="document.uuid !== ''" class="row">
                                            <label class="col-form-label col-lg-2">Ursprungligt filnamn:</label>
                                            <div class="col-lg-10">
                                                <div class="form-group">
                                                    <input type="text" class="form-control" v-model="document.original_name" readonly>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="row">
                                            <label class="col-form-label col-lg-2">Kategori: <span class="text-danger">*</span></label>
                                            <div class="col-lg-10">
                                                <select-with-validation
                                                    vid="document_category_id"
                                                    label="Kategori"
                                                    rules="required"
                                                    v-model="document.document_category_id"
                                                    :options="categories"
                                                    :reduce="item => item.id"
                                                    option-label="name"
                                                    placeholder="Välj..."
                                                    hide-label
                                                />
                                            </div>
                                        </div>
                                        <div class="row">
                                            <label class="col-form-label col-lg-2">{{ (document.uuid === '' ? 'Fil' : 'Ladda upp ny version') }}: <span v-if="document.uuid === ''" class="text-danger">*</span></label>
                                            <div class="col-lg-10">
                                                <ValidationProvider vid="file" name="Fil" rules="" v-slot="{ errors }">
                                                    <div class="form-group">
                                                        <limitless-file-input @change="inputFileChanged" ref="fileInput"></limitless-file-input>
                                                        <span class="validation-invalid-label">{{ errors[0] }}</span>
                                                    </div>
                                                </ValidationProvider>
                                            </div>
                                        </div>
                                    </form>
                                </ValidationObserver>
                            </div>
                            <div class="card-footer">
                                <div class="d-flex justify-content-end">
                                    <limitless-button v-if="document.uuid !== ''" ref="btndeletedocument" type="button" @click="deleteDocument" :busy="busy_saving" icon="icon-cross2" btn-class="btn-danger" class="mr-auto">RADERA</limitless-button>
                                    <limitless-button ref="btnsavedocument" type="button" @click="saveDocument" :busy="busy_saving" icon="icon-floppy-disk">SPARA</limitless-button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div> <!-- /content -->
    </div>
</template>

<script>
import Vue from 'vue';
import apiClient from '@services/ApiClient';
import LimitlessPageHeader from '@components/Layout/partials/LimitlessPageHeader';
import LimitlessPageHeaderButton from '@components/Layout/partials/LimitlessPageHeaderButton';
import LimitlessFileInput from '@components/Limitless/LimitlessFileInputComponent';
import LimitlessButton from '@components/Limitless/LimitlessButton';
import getError from '@services/ErrorHelper';
import InputWithValidation from '@components/Form/InputWithValidation.vue';
import SelectWithValidation from '@components/Form/SelectWithValidation.vue';

export default {
    name: 'DocumentEdit',
    components: {
        SelectWithValidation,
        InputWithValidation,
        LimitlessButton,
        LimitlessPageHeader,
        LimitlessPageHeaderButton,
        LimitlessFileInput,
    },
    props: {
        uuid: {
            type: String,
            default: '',
        },
    },
    data: () => ({
        busy_loading: true,
        busy_saving: false,
        categories: [],
        document: {
            uuid: '',
            name: '',
            document_category_id: '',
        },
        files: null,
    }),
    mounted() {
        this.getCategories();
        this.init();
    },
    watch: {
        id() {
            this.init();
        },
    },
    methods: {
        init() {
            this.getDocument();
        },
        getDocument() {
            const vm = this;
            if (this.uuid) {
                vm.busy_loading = true;
                apiClient.get('/vue/documents/' + this.uuid + '')
                    .then((response) => {
                        vm.document = response.data.document;
                    })
                    .finally(() => {
                        vm.busy_loading = false;
                    });
            } else {
                vm.busy_loading = false;
            }
        },
        async saveDocument() {
            const vm = this;
            const isValid = await this.$refs.documentform.validate();
            if (isValid) {
                vm.busy_saving = true;
                let config = {};
                const formData = new FormData();
                Object.keys(vm.document).forEach((key) => formData.append(key, vm.document[key]));
                if (vm.files) {
                    formData.append('file', vm.files[0]);
                    config = {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                        },
                    };
                }
                await apiClient.post('/vue/documents/' + (vm.uuid ? vm.uuid : 'new'), formData, config)
                    .then((response) => {
                        if (vm.uuid !== response.data.document.uuid) {
                            window.location.href = '/documents/' + response.data.document.uuid;
                        }
                        vm.document = response.data.document;
                        vm.$refs.btnsavedocument.triggerSaved();
                    })
                    .catch((error) => {
                        const parsederror = getError(error);
                        if (typeof parsederror === 'object') {
                            vm.$refs.documentform.setErrors(parsederror);
                        }
                        vm.$refs.btnsavedocument.triggerFailed();
                    })
                    .finally(() => {
                        vm.busy_saving = false;
                    });
            }
        },
        deleteDocument() {
            const vm = this;
            // eslint-disable-next-line no-restricted-globals,no-alert
            if (confirm('Are you sure you want to delete this document?')) {
                vm.busy_saving = true;
                apiClient.delete('/vue/documents/' + this.uuid)
                    .then(() => {
                        document.location.href = '/documents';
                        Vue.set(vm, 'busy_saving', false);
                    })
                    .catch(() => {
                        vm.$refs.btndeletedocument.triggerFailed();
                    })
                    .finally(() => {
                        vm.busy_saving = false;
                    });
            }
        },
        inputFileChanged(files) {
            this.files = files;
        },
        getCategories() {
            apiClient.get('/vue/documents/categories/table').then((res) => {
                this.categories = res.data.data;
            });
        },
    },
    computed: {
        document_name() {
            return (this.document && this.document.id > 0 ? this.document.title : 'New document');
        },
        document_id() {
            return (this.document && this.document.id > 0 ? this.document.id : '');
        },
    },
};

</script>
